import { useState, useCallback, useEffect, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { DataGrid, type DataGridColumn } from "@/components/ui/DataGrid"
import { Card, CardContent } from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import { Input, SearchInput } from "@/components/ui/Input"
import { Badge } from "@/components/ui/Badge"
import { useClients, useConvertLeadToClient, useUpdateClient, useCreateNpsSurvey } from "@/hooks/useCustomerSuccess"
import { useCompanyContacts, useCreateContact, useUpdateContact } from "@/hooks/useLeads"
import api from "@/lib/api"
import type { CsClient, CsClientUpdate } from "@/types/customerSuccess"
import type { Contact, ContactCreate, ContactUpdate } from "@/types"
import { formatDate } from "@/lib/utils"
import {
  Building2, Search, Loader2, UserPlus, X, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, AlertTriangle, Clock, Edit2, Users, MoreHorizontal,
  Plus, Mail, Phone, Briefcase, ToggleLeft, ToggleRight, Send, ExternalLink,
  Shield, ShieldCheck, ShieldOff, ShieldAlert,
} from "lucide-react"

// ==========================================
// Constants
// ==========================================

const CONTRACT_STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' | 'outline'; icon: any }> = {
  active: { label: "Ativo", variant: "success", icon: CheckCircle },
  suspended: { label: "Suspenso", variant: "warning", icon: AlertTriangle },
  cancelled: { label: "Cancelado", variant: "destructive", icon: XCircle },
  prospect: { label: "Prospect", variant: "secondary", icon: Clock },
}

const SEGMENTS = [
  "Financeiro", "Varejo", "Industria", "Saude", "Educacao",
  "Governo", "Tecnologia", "Servicos", "Agronegocio", "Energia",
]

const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"

function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { detail?: string }, status?: number } }
  if (axiosErr?.response?.data?.detail) return axiosErr.response.data.detail
  return fallback
}

// ==========================================
// Stat Card
// ==========================================

function StatCard({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: number | string }) {
  return (
    <Card><CardContent className="p-3">
      <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-muted-foreground" /><span className="text-xs text-muted-foreground">{label}</span></div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </CardContent></Card>
  )
}

// ==========================================
// Row Actions Dropdown
// ==========================================

function RowActions({ client, onEdit, onContacts, onNewOpp, onSendNps }: {
  client: CsClient
  onEdit: () => void
  onContacts: () => void
  onNewOpp: () => void
  onSendNps: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="icon" onClick={() => setOpen(v => !v)} title="Acoes">
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-44">
          <button onClick={() => { onEdit(); setOpen(false) }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-750 flex items-center gap-2">
            <Edit2 className="h-3.5 w-3.5" /> Editar Cliente
          </button>
          <button onClick={() => { onContacts(); setOpen(false) }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-750 flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Contatos
          </button>
          <button onClick={() => { onNewOpp(); setOpen(false) }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-750 flex items-center gap-2">
            <ExternalLink className="h-3.5 w-3.5" /> Nova Oportunidade
          </button>
          <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
          <button onClick={() => { onSendNps(); setOpen(false) }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-750 flex items-center gap-2 text-blue-600 dark:text-blue-400">
            <Send className="h-3.5 w-3.5" /> Enviar NPS
          </button>
        </div>
      )}
    </div>
  )
}

// ==========================================
// Edit Client Modal
// ==========================================

function EditClientModal({ client, onClose }: { client: CsClient; onClose: () => void }) {
  const updateClient = useUpdateClient()
  const [form, setForm] = useState({
    contract_status: client.contract_status || "active",
    client_since: client.client_since?.split("T")[0] || "",
  })
  const [error, setError] = useState("")

  const handleSave = async () => {
    setError("")
    try {
      await updateClient.mutateAsync({
        companyId: client.id,
        data: {
          contract_status: form.contract_status as any,
          client_since: form.client_since || null,
        },
      })
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao salvar"))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Editar Cliente</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <p className="font-medium mb-1">{client.name}</p>
            {client.cnpj && <p className="text-xs text-muted-foreground font-mono">{client.cnpj}</p>}
          </div>

          <div>
            <label className="text-sm font-medium">Status do Contrato</label>
            <select value={form.contract_status} onChange={e => setForm(f => ({ ...f, contract_status: e.target.value }))} className={selectClass}>
              <option value="active">Ativo</option>
              <option value="suspended">Suspenso</option>
              <option value="cancelled">Cancelado</option>
              <option value="prospect">Prospect</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">Cliente Desde</label>
            <Input type="date" value={form.client_since} onChange={e => setForm(f => ({ ...f, client_since: e.target.value }))} />
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={updateClient.isPending}>
            {updateClient.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  )
}

// ==========================================
// Contact Toggle NPS
// ==========================================

function NpsToggle({ contact, onToggle }: { contact: Contact; onToggle: (enabled: boolean) => void }) {
  const enabled = (contact as any).nps_enabled === true
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(!enabled) }}
      className={"flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all " + (
        enabled
          ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900"
          : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
      )}
      title={enabled ? "Recebe campanhas NPS" : "NPS desabilitado"}
    >
      {enabled ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
      {enabled ? "NPS Ativo" : "NPS Inativo"}
    </button>
  )
}

// ==========================================
// Contacts SlideOver
// ==========================================

function ContactsSlideOver({ client, onClose }: { client: CsClient; onClose: () => void }) {
  const { data: contacts, isLoading, refetch } = useCompanyContacts(client.id)
  const updateContact = useUpdateContact()
  const [showAddContact, setShowAddContact] = useState(false)

  const handleToggleNps = async (contact: Contact, enabled: boolean) => {
    try {
      await updateContact.mutateAsync({
        contactId: contact.id,
        data: { nps_enabled: enabled } as ContactUpdate,
      })
      refetch()
    } catch (err) {
      console.error("Error toggling NPS:", err)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      {/* Backdrop */}
      <div className="flex-1 bg-black/40" />
      {/* Panel */}
      <div className="w-full max-w-lg bg-card border-l shadow-2xl flex flex-col h-full" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Contatos
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">{client.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setShowAddContact(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Novo Contato
            </Button>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* NPS Legend */}
        <div className="px-5 py-2 border-b bg-blue-50/50 dark:bg-blue-950/20">
          <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Contatos com <strong>NPS Ativo</strong> receberao as campanhas de pesquisa NPS/CSAT automaticamente.</span>
          </div>
        </div>

        {/* Contact List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : !contacts || contacts.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="font-medium">Nenhum contato</p>
              <p className="text-sm mt-1">Adicione contatos para este cliente.</p>
            </div>
          ) : (
            <div className="divide-y">
              {contacts.map((c: Contact) => (
                <div key={c.id} className="px-5 py-3 hover:bg-muted/30 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{c.display_name || c.full_name || c.first_name}</span>
                        {c.is_primary && <Badge variant="info" className="text-[10px] px-1.5 py-0">Principal</Badge>}
                      </div>
                      {c.job_title && (
                        <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                          <Briefcase className="w-3 h-3" /> {c.job_title}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3 mt-1">
                        {c.email && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="w-3 h-3" /> {c.email}
                          </span>
                        )}
                        {(c.phone || c.mobile) && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone className="w-3 h-3" /> {c.phone || c.mobile}
                          </span>
                        )}
                      </div>
                    </div>
                    <NpsToggle contact={c} onToggle={(enabled) => handleToggleNps(c, enabled)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Contact Modal */}
      {showAddContact && (
        <AddContactModal companyId={client.id} onClose={() => { setShowAddContact(false); refetch() }} />
      )}
    </div>
  )
}

// ==========================================
// Add Contact Modal
// ==========================================

function AddContactModal({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const createContact = useCreateContact()
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    job_title: "",
    phone: "",
    nps_enabled: false,
  })
  const [error, setError] = useState("")

  const handleSave = async () => {
    if (!form.first_name.trim()) { setError("Nome e obrigatorio"); return }
    if (!form.email.trim()) { setError("Email e obrigatorio"); return }
    setError("")
    try {
      await createContact.mutateAsync({
        company_id: companyId,
        first_name: form.first_name,
        last_name: form.last_name || null,
        email: form.email || null,
        job_title: form.job_title || null,
        phone: form.phone || null,
        nps_enabled: form.nps_enabled,
      } as ContactCreate)
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao criar contato"))
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Novo Contato</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Nome *</label>
              <Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="Nome" />
            </div>
            <div>
              <label className="text-sm font-medium">Sobrenome</label>
              <Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Sobrenome" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Email *</label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@empresa.com" />
          </div>

          <div>
            <label className="text-sm font-medium">Cargo</label>
            <Input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} placeholder="Ex: Diretor de TI" />
          </div>

          <div>
            <label className="text-sm font-medium">Telefone</label>
            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(11) 99999-9999" />
          </div>

          {/* NPS Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-2">
              <ShieldCheck className={"w-4 h-4 " + (form.nps_enabled ? "text-green-500" : "text-gray-400")} />
              <div>
                <p className="text-sm font-medium">Recebe campanhas NPS</p>
                <p className="text-xs text-muted-foreground">Contato sera incluido nas pesquisas NPS/CSAT</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, nps_enabled: !f.nps_enabled }))}
              className={"relative inline-flex h-6 w-11 items-center rounded-full transition-colors " + (form.nps_enabled ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600")}
            >
              <span className={"inline-block h-4 w-4 transform rounded-full bg-white transition-transform " + (form.nps_enabled ? "translate-x-6" : "translate-x-1")} />
            </button>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={createContact.isPending}>
            {createContact.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Criar Contato
          </Button>
        </div>
      </div>
    </div>
  )
}

// ==========================================
// Convert Lead Modal (improved)
// ==========================================

function ConvertLeadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [search, setSearch] = useState("")
  const [leads, setLeads] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [converting, setConverting] = useState<string | null>(null)
  const convertLead = useConvertLeadToClient()
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (!open) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true)
      try {
        const params: Record<string, string | number> = { page: 1, per_page: 20 }
        if (search.trim()) params.search = search.trim()
        const { data } = await api.get("/v1/leads/", { params })
        setLeads(data?.items || [])
      } catch (err) {
        console.error("[ConvertLeadModal] Error fetching leads:", err)
        setLeads([])
      } finally {
        setIsLoading(false)
      }
    }, search ? 400 : 0)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [open, search])

  useEffect(() => {
    if (!open) { setSearch(""); setLeads([]); setConverting(null) }
  }, [open])

  if (!open) return null

  const handleConvert = async (lead: any) => {
    setConverting(lead.id)
    try {
      await convertLead.mutateAsync({ leadId: lead.id })
      onClose()
    } catch (err) {
      console.error("Error converting lead:", err)
      setConverting(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Converter Lead em Cliente</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar lead por nome..." className="pl-9" />
        </div>
        <div className="flex-1 overflow-y-auto divide-y min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : leads.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <UserPlus className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>Nenhum lead encontrado.</p>
              <p className="text-sm mt-1">Crie um lead primeiro na aba Leads.</p>
            </div>
          ) : (
            leads.map((lead: any) => (
              <div key={lead.id} className="py-3 px-2 rounded transition-colors hover:bg-muted/50">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1 mr-3">
                    <p className="font-medium truncate">{lead.company?.name || lead.title || "---"}</p>
                    {lead.primary_contact?.name && (
                      <p className="text-xs text-muted-foreground truncate">
                        {lead.primary_contact.name}
                        {lead.primary_contact.email && <> - {lead.primary_contact.email}</>}
                      </p>
                    )}
                  </div>
                  <Button size="sm" className="shrink-0" onClick={() => handleConvert(lead)} disabled={converting === lead.id}>
                    {converting === lead.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />}
                    Converter
                  </Button>
                </div>
                {/* Info about what will happen */}
                {lead.primary_contact?.name && (
                  <div className="mt-2 p-2 rounded bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200/50 dark:border-blue-800/50">
                    <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3 shrink-0" />
                      O contato principal <strong>{lead.primary_contact.name}</strong> sera habilitado para receber campanhas NPS.
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ==========================================
// Send NPS Modal
// ==========================================

function SendNpsModal({ client, onClose }: { client: CsClient; onClose: () => void }) {
  const { data: contacts, isLoading } = useCompanyContacts(client.id)
  const createSurvey = useCreateNpsSurvey()
  const [selectedContact, setSelectedContact] = useState<string>("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  const npsContacts = useMemo(() => {
    return (contacts || []).filter((c: Contact) => (c as any).nps_enabled && c.email)
  }, [contacts])

  const handleSend = async () => {
    if (!selectedContact) { setError("Selecione um contato"); return }
    setError("")
    try {
      await createSurvey.mutateAsync({
        company_id: client.id,
        contact_id: selectedContact,
        survey_type: "nps_relational",
      })
      setSuccess(true)
      setTimeout(onClose, 1500)
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao enviar pesquisa NPS"))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Enviar NPS</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-sm text-muted-foreground">Enviar pesquisa NPS avulsa para <strong>{client.name}</strong></p>

        {isLoading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : npsContacts.length === 0 ? (
          <div className="p-3 rounded-lg border bg-amber-50/50 dark:bg-amber-950/20 text-sm text-amber-700 dark:text-amber-300">
            <p className="flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Nenhum contato com NPS habilitado.</p>
            <p className="mt-1 text-xs">Acesse "Contatos" para habilitar o NPS em pelo menos um contato.</p>
          </div>
        ) : (
          <div>
            <label className="text-sm font-medium">Contato destinatario</label>
            <select value={selectedContact} onChange={e => setSelectedContact(e.target.value)} className={selectClass}>
              <option value="">Selecione...</option>
              {npsContacts.map((c: Contact) => (
                <option key={c.id} value={c.id}>{c.display_name || c.first_name} - {c.email}</option>
              ))}
            </select>
          </div>
        )}

        {success && <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Pesquisa NPS criada com sucesso!</p>}
        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSend} disabled={createSurvey.isPending || npsContacts.length === 0 || success}>
            {createSurvey.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Enviar
          </Button>
        </div>
      </div>
    </div>
  )
}

// ==========================================
// Main Page
// ==========================================

export default function ClientsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [filterStatus, setFilterStatus] = useState("")
  const [filterSegment, setFilterSegment] = useState("")
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [editClient, setEditClient] = useState<CsClient | null>(null)
  const [contactsClient, setContactsClient] = useState<CsClient | null>(null)
  const [npsClient, setNpsClient] = useState<CsClient | null>(null)

  const { data, isLoading } = useClients({
    page: 1,
    perPage: 500,
    search: undefined,
    status: undefined,
  })

  const filteredClients = useMemo(() => {
    let items = data?.items || []
    if (search) {
      const s = search.toLowerCase()
      items = items.filter(c =>
        (c.name || "").toLowerCase().includes(s) ||
        (c.trading_name || "").toLowerCase().includes(s) ||
        (c.cnpj || "").toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s)
      )
    }
    if (filterStatus) items = items.filter(c => c.contract_status === filterStatus)
    if (filterSegment) items = items.filter(c => c.segment === filterSegment)
    return items
  }, [data?.items, search, filterStatus, filterSegment])

  const stats = useMemo(() => {
    const items = data?.items || []
    return {
      total: items.length,
      active: items.filter(c => c.contract_status === "active").length,
      suspended: items.filter(c => c.contract_status === "suspended").length,
      prospect: items.filter(c => c.contract_status === "prospect").length,
    }
  }, [data?.items])

  const columns: DataGridColumn<CsClient>[] = [
    {
      id: "name",
      header: "Empresa",
      accessor: (c) => (
        <div>
          <div className="font-medium">{c.trading_name || c.name}</div>
          {c.trading_name && c.name !== c.trading_name && <div className="text-xs text-muted-foreground">{c.name}</div>}
        </div>
      ),
      sortValue: (c) => c.trading_name || c.name || "",
      filterValue: (c) => (c.trading_name || "") + " " + (c.name || ""),
    },
    {
      id: "cnpj",
      header: "CNPJ",
      accessor: (c) => <span className="font-mono text-xs text-muted-foreground">{c.cnpj || "---"}</span>,
      sortValue: (c) => c.cnpj || "",
      filterValue: (c) => c.cnpj || "",
    },
    {
      id: "client_since",
      header: "Desde",
      accessor: (c) => <span className="text-muted-foreground text-xs">{c.client_since ? formatDate(c.client_since) : "---"}</span>,
      sortValue: (c) => c.client_since ? new Date(c.client_since).getTime() : 0,
      filterValue: (c) => c.client_since ? formatDate(c.client_since) : "",
    },
    {
      id: "status",
      header: "Status",
      accessor: (c) => {
        const cfg = CONTRACT_STATUS[c.contract_status || "prospect"] || CONTRACT_STATUS.prospect
        return <Badge variant={cfg.variant}>{cfg.label}</Badge>
      },
      sortValue: (c) => c.contract_status || "prospect",
      filterValue: (c) => CONTRACT_STATUS[c.contract_status || "prospect"]?.label || "Prospect",
    },
    {
      id: "segment",
      header: "Segmento",
      accessor: (c) => c.segment ? <Badge variant="outline">{c.segment}</Badge> : <span className="text-muted-foreground">---</span>,
      sortValue: (c) => c.segment || "",
      filterValue: (c) => c.segment || "",
    },
    {
      id: "location",
      header: "Cidade/UF",
      accessor: (c) => (
        <span className="text-muted-foreground text-xs">
          {c.city && c.state ? c.city + "/" + c.state : c.city || c.state || "---"}
        </span>
      ),
      sortValue: (c) => (c.state || "") + (c.city || ""),
      filterValue: (c) => (c.city || "") + " " + (c.state || ""),
    },
    {
      id: "contact",
      header: "Contato",
      accessor: (c) => (
        <div>
          <div className="text-sm">{c.email || "---"}</div>
          {c.phone && <div className="text-xs text-muted-foreground">{c.phone}</div>}
        </div>
      ),
      sortValue: (c) => c.email || "",
      filterValue: (c) => (c.email || "") + " " + (c.phone || ""),
    },
    {
      id: "actions",
      header: "Acoes",
      sortable: false,
      filterable: false,
      align: "right" as const,
      accessor: (c) => (
        <div onClick={(e: any) => e.stopPropagation()}>
          <RowActions
            client={c}
            onEdit={() => setEditClient(c)}
            onContacts={() => setContactsClient(c)}
            onNewOpp={() => navigate("/opportunities?company_id=" + c.id)}
            onSendNps={() => setNpsClient(c)}
          />
        </div>
      ),
    },
  ]

  return (
    <div className="min-h-screen">
      <div className="p-6 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Building2} label="Total Clientes" value={stats.total} />
          <StatCard icon={CheckCircle} label="Ativos" value={stats.active} />
          <StatCard icon={AlertTriangle} label="Suspensos" value={stats.suspended} />
          <StatCard icon={Clock} label="Prospects" value={stats.prospect} />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 flex-wrap">
            <div className="w-64">
              <SearchInput placeholder="Buscar empresa, CNPJ..." value={search} onChange={(e) => setSearch(e.target.value)} onClear={() => setSearch("")} />
            </div>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">Todos os status</option>
              <option value="active">Ativo</option>
              <option value="suspended">Suspenso</option>
              <option value="cancelled">Cancelado</option>
              <option value="prospect">Prospect</option>
            </select>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterSegment} onChange={(e) => setFilterSegment(e.target.value)}>
              <option value="">Todos os segmentos</option>
              {SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Button onClick={() => setShowConvertModal(true)} leftIcon={<UserPlus className="h-4 w-4" />}>Converter Lead</Button>
        </div>

        {/* Grid */}
        <Card>
          <CardContent className="p-0">
            <DataGrid
              gridId="cs-clients"
              columns={columns}
              data={filteredClients}
              isLoading={isLoading}
              emptyMessage="Nenhum cliente encontrado"
              emptyIcon={<Building2 className="w-10 h-10 opacity-20" />}
              pageSize={25}
            />
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <ConvertLeadModal open={showConvertModal} onClose={() => setShowConvertModal(false)} />
      {editClient && <EditClientModal client={editClient} onClose={() => setEditClient(null)} />}
      {contactsClient && <ContactsSlideOver client={contactsClient} onClose={() => setContactsClient(null)} />}
      {npsClient && <SendNpsModal client={npsClient} onClose={() => setNpsClient(null)} />}
    </div>
  )
}
